import { z } from "zod";
import { voiceGuesserCandidateSchema } from "./candidate.js";
import { voiceGuesserCancelReasonSchema, voiceGuesserReactionEmojiSchema, voiceGuesserRecordingsPerPlayerSchema } from "./game.js";
import { voiceGuesserPlayerSchema } from "./player.js";
import { voiceGuesserPerPlayerRoundResultSchema, voiceGuesserRankingEntrySchema, voiceGuesserRoundSchema } from "./round.js";

export const VOICE_GUESSER_ENVELOPE_TYPES = {
	LOBBY_UPDATED: "vg_lobby_updated",
	RECORDING_STARTED: "vg_recording_started",
	RECORDING_PROGRESS: "vg_recording_progress",
	RECORDING_TURN_ENDED: "vg_recording_turn_ended",
	GAME_STARTED: "vg_game_started",
	ROUND_STARTED: "vg_round_started",
	ANSWER_RECEIVED: "vg_answer_received",
	VOTE_REVEALED: "vg_vote_revealed",
	ROUND_ENDED: "vg_round_ended",
	GAME_ENDED: "vg_game_ended",
	GAME_CANCELED: "vg_game_canceled",
	REACTION_RECEIVED: "vg_reaction_received",
	TAP_RECEIVED: "vg_tap_received",
} as const;

export type VoiceGuesserEnvelopeType = (typeof VOICE_GUESSER_ENVELOPE_TYPES)[keyof typeof VOICE_GUESSER_ENVELOPE_TYPES];

export const voiceGuesserLobbyUpdatedSchema = z.object({
	type: z.literal(VOICE_GUESSER_ENVELOPE_TYPES.LOBBY_UPDATED),
	game_id: z.string(),
	host_user_id: z.string(),
	players: z.array(voiceGuesserPlayerSchema),
	recordings_per_player: voiceGuesserRecordingsPerPlayerSchema,
	auto_start_at: z.number().int().positive().nullable(),
});
export type VoiceGuesserLobbyUpdatedEnvelope = z.infer<typeof voiceGuesserLobbyUpdatedSchema>;

export const voiceGuesserRecordingStartedSchema = z.object({
	type: z.literal(VOICE_GUESSER_ENVELOPE_TYPES.RECORDING_STARTED),
	game_id: z.string(),
	phrase: z.string(),
	started_at: z.number().int().positive(),
	ends_at: z.number().int().positive(),
	turn_index: z.number().int().nonnegative(),
	total_turns: z.number().int().positive(),
	started_player_count: z.number().int().positive(),
});
export type VoiceGuesserRecordingStartedEnvelope = z.infer<typeof voiceGuesserRecordingStartedSchema>;

export const voiceGuesserRecordingProgressSchema = z.object({
	type: z.literal(VOICE_GUESSER_ENVELOPE_TYPES.RECORDING_PROGRESS),
	game_id: z.string(),
	user_id: z.string(),
	validated: z.boolean(),
	validated_count: z.number().int().nonnegative(),
	total_count: z.number().int().nonnegative(),
	turn_index: z.number().int().nonnegative(),
});
export type VoiceGuesserRecordingProgressEnvelope = z.infer<typeof voiceGuesserRecordingProgressSchema>;

export const voiceGuesserRecordingTurnEndedSchema = z.object({
	type: z.literal(VOICE_GUESSER_ENVELOPE_TYPES.RECORDING_TURN_ENDED),
	game_id: z.string(),
	turn_index: z.number().int().nonnegative(),
	validated_user_ids: z.array(z.string()),
});
export type VoiceGuesserRecordingTurnEndedEnvelope = z.infer<typeof voiceGuesserRecordingTurnEndedSchema>;

export const voiceGuesserGameStartedSchema = z.object({
	type: z.literal(VOICE_GUESSER_ENVELOPE_TYPES.GAME_STARTED),
	game_id: z.string(),
	rounds_count: z.number().int().positive(),
	candidates: z.array(voiceGuesserCandidateSchema),
});
export type VoiceGuesserGameStartedEnvelope = z.infer<typeof voiceGuesserGameStartedSchema>;

export const voiceGuesserRoundStartedSchema = z.object({
	type: z.literal(VOICE_GUESSER_ENVELOPE_TYPES.ROUND_STARTED),
	game_id: z.string(),
	round: z.number().int().nonnegative(),
	audio_id: voiceGuesserRoundSchema.shape.audio_id,
	audio_url: voiceGuesserRoundSchema.shape.audio_url,
	effect: voiceGuesserRoundSchema.shape.effect,
	started_at: z.number().int().positive(),
	ends_at: z.number().int().positive(),
	options: voiceGuesserRoundSchema.shape.options,
});
export type VoiceGuesserRoundStartedEnvelope = z.infer<typeof voiceGuesserRoundStartedSchema>;

export const voiceGuesserAnswerReceivedSchema = z.object({
	type: z.literal(VOICE_GUESSER_ENVELOPE_TYPES.ANSWER_RECEIVED),
	game_id: z.string(),
	round: z.number().int().nonnegative(),
	user_id: z.string(),
	answered_at: z.number().int().positive(),
});
export type VoiceGuesserAnswerReceivedEnvelope = z.infer<typeof voiceGuesserAnswerReceivedSchema>;

export const voiceGuesserVoteRevealedSchema = z.object({
	type: z.literal(VOICE_GUESSER_ENVELOPE_TYPES.VOTE_REVEALED),
	game_id: z.string(),
	round: z.number().int().nonnegative(),
	user_id: z.string(),
	guessed_user_id: z.string(),
});
export type VoiceGuesserVoteRevealedEnvelope = z.infer<typeof voiceGuesserVoteRevealedSchema>;

export const voiceGuesserRoundEndedSchema = z.object({
	type: z.literal(VOICE_GUESSER_ENVELOPE_TYPES.ROUND_ENDED),
	game_id: z.string(),
	round: z.number().int().nonnegative(),
	correct_user_id: z.string(),
	scores: z.record(z.string(), z.number().int().nonnegative()),
	per_player_round: z.array(voiceGuesserPerPlayerRoundResultSchema),
	session_leaderboard: z.array(voiceGuesserRankingEntrySchema),
});
export type VoiceGuesserRoundEndedEnvelope = z.infer<typeof voiceGuesserRoundEndedSchema>;

export const voiceGuesserGameEndedSchema = z.object({
	type: z.literal(VOICE_GUESSER_ENVELOPE_TYPES.GAME_ENDED),
	game_id: z.string(),
	scores: z.record(z.string(), z.number().int().nonnegative()),
	ranking: z.array(voiceGuesserRankingEntrySchema),
});
export type VoiceGuesserGameEndedEnvelope = z.infer<typeof voiceGuesserGameEndedSchema>;

export const voiceGuesserGameCanceledSchema = z.object({
	type: z.literal(VOICE_GUESSER_ENVELOPE_TYPES.GAME_CANCELED),
	game_id: z.string(),
	reason: voiceGuesserCancelReasonSchema,
});
export type VoiceGuesserGameCanceledEnvelope = z.infer<typeof voiceGuesserGameCanceledSchema>;

export const voiceGuesserReactionReceivedSchema = z.object({
	type: z.literal(VOICE_GUESSER_ENVELOPE_TYPES.REACTION_RECEIVED),
	game_id: z.string(),
	reaction_id: z.string(),
	user_id: z.string(),
	display_name: z.string(),
	avatar_url: z.string().nullable(),
	emoji: voiceGuesserReactionEmojiSchema,
	created_at: z.number().int().positive(),
});
export type VoiceGuesserReactionReceivedEnvelope = z.infer<typeof voiceGuesserReactionReceivedSchema>;

export const voiceGuesserTapReceivedSchema = z.object({
	type: z.literal(VOICE_GUESSER_ENVELOPE_TYPES.TAP_RECEIVED),
	game_id: z.string(),
	from_user_id: z.string(),
	from_display_name: z.string(),
	from_avatar_url: z.string().nullable(),
	target_user_id: z.string(),
	created_at: z.number().int().positive(),
});
export type VoiceGuesserTapReceivedEnvelope = z.infer<typeof voiceGuesserTapReceivedSchema>;

export const voiceGuesserEnvelopeSchemas = [
	voiceGuesserLobbyUpdatedSchema,
	voiceGuesserRecordingStartedSchema,
	voiceGuesserRecordingProgressSchema,
	voiceGuesserRecordingTurnEndedSchema,
	voiceGuesserGameStartedSchema,
	voiceGuesserRoundStartedSchema,
	voiceGuesserAnswerReceivedSchema,
	voiceGuesserVoteRevealedSchema,
	voiceGuesserRoundEndedSchema,
	voiceGuesserGameEndedSchema,
	voiceGuesserGameCanceledSchema,
	voiceGuesserReactionReceivedSchema,
	voiceGuesserTapReceivedSchema,
] as const;

export const VOICE_GUESSER_ENVELOPE_TYPE_SET = new Set<string>(Object.values(VOICE_GUESSER_ENVELOPE_TYPES));

export type VoiceGuesserServerEnvelope =
	| VoiceGuesserLobbyUpdatedEnvelope
	| VoiceGuesserRecordingStartedEnvelope
	| VoiceGuesserRecordingProgressEnvelope
	| VoiceGuesserRecordingTurnEndedEnvelope
	| VoiceGuesserGameStartedEnvelope
	| VoiceGuesserRoundStartedEnvelope
	| VoiceGuesserAnswerReceivedEnvelope
	| VoiceGuesserVoteRevealedEnvelope
	| VoiceGuesserRoundEndedEnvelope
	| VoiceGuesserGameEndedEnvelope
	| VoiceGuesserGameCanceledEnvelope
	| VoiceGuesserReactionReceivedEnvelope
	| VoiceGuesserTapReceivedEnvelope;
